<template>
	<div class="content-page content-page--transaction-details">
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					{{ transaction.logTypeFormated }} Transaction Details
				</h2>
			</div>
		</div>
		<Spinner :processor="{ loading: loadingDetails }" />
		<ScrollContainer>
			<div
				class="transaction-details"
				v-if="hasResultDetail && !loadingDetails"
			>
				<div class="transaction-details__header">
					<div class="transaction-details__header-primary">
						<div class="transaction-details__header-status">
							<NotificationIcon
								:variant="notificationVariantIcon"
								@mouseenter="tooltipStatusVisible = true"
								@focus="tooltipStatusVisible = true"
								@mouseleave="tooltipStatusVisible = false"
								@blur="tooltipStatusVisible = false"
								ref="tooltipStatusRef"
							/>
							<Tooltip
								:text="transaction.health"
								:trackedRefName="'tooltipStatusRef'"
								:display="tooltipStatusVisible"
								:offset="[4, 4]"
								position="top-start"
							/>
						</div>
						<div class="transaction-details__header-title-group">
							<span class="transaction-details__header-type">
								{{ transaction.logTypeFormated }}
								{{ transaction.oemManufacturer }}
							</span>
							<div class="transaction-details__header-integrator">
								<!-- <a class="link" data-test="detailLink">{{
								transaction.integratorName
							}}</a> -->
								{{ integratorName }}
							</div>
						</div>
					</div>

					<div class="transaction-details__header-secondary">
						<span class="transaction-details__header-date">{{
							shortDateString(transaction.timestamp)
						}}</span>
						<span class="transaction-details__header-time">
							{{ timeAndTimeZone(transaction.timestamp) }}
						</span>
					</div>
				</div>

				<ul class="transaction-details__details">
					<li
						class="transaction-details__details-item"
						v-if="transaction.transactionId"
					>
						<span class="transaction-details__details-label"
							>Transaction ID:</span
						>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="transaction.transactionId"
						></Ellipsis>
					</li>

					<li
						class="transaction-details__details-item"
						v-if="transaction.correlationId"
					>
						<span class="transaction-details__details-label"
							>Correlation ID:</span
						>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="transaction.correlationId"
						></Ellipsis>
					</li>

					<li
						class="transaction-details__details-item"
						v-if="customerName"
					>
						<span class="transaction-details__details-label"
							>Client:</span
						>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="customerName"
						></Ellipsis>
					</li>

					<li class="transaction-details__details-item">
						<span class="transaction-details__details-label"
							>Company:</span
						>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="transaction.companyName"
						></Ellipsis>
					</li>

					<!-- DEV NOTE: "Integration" data should be shown on both 3P Vendor and OEM transaction types -->
					<li
						class="transaction-details__details-item"
						v-if="
							!isVueDmsInternalType && transaction.integrationName
						"
					>
						<span class="transaction-details__details-label"
							>Integration:</span
						>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="transaction.integrationName"
						></Ellipsis>
					</li>

					<!-- DEV NOTE: "Method" data should be shown on both 3P Vendor and OEM transaction types -->
					<li class="transaction-details__details-item">
						<!-- <li
					class="transaction-details__details-item"
					v-if="
						is3pVendorTransactionType &&
							transaction.thirdPartyVendorMethod
					"
				> -->
						<span class="transaction-details__details-label"
							>Method:</span
						>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="transaction.thirdPartyVendorMethod"
						></Ellipsis>
					</li>

					<li
						class="transaction-details__details-item"
						v-if="isOEMTransactionType && transaction.oemMethod"
					>
						<span class="transaction-details__details-label"
							>Method:</span
						>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="transaction.oemMethod"
						></Ellipsis>
					</li>
					<!--  -->

					<!-- DEV NOTE: "Direction" data should be shown on both 3P Vendor and OEM transaction types -->
					<li
						class="transaction-details__details-item"
						v-if="!isVueDmsInternalType && transaction.direction"
					>
						<span class="transaction-details__details-label"
							>Direction:</span
						>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="transaction.direction"
						></Ellipsis>
					</li>

					<li
						class="transaction-details__details-item"
						v-if="isVueDmsInternalType && transaction.userid"
					>
						<span class="transaction-details__details-label"
							>User ID:</span
						>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="transaction.userid"
						></Ellipsis>
					</li>

					<li
						class="transaction-details__details-item"
						v-if="isVueDmsInternalType && transaction.source"
					>
						<span class="transaction-details__details-label"
							>Source:
						</span>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="transaction.source"
						/>
					</li>

					<li
						class="transaction-details__details-item"
						v-if="isVueDmsInternalType && transaction.requestType"
					>
						<span class="transaction-details__details-label"
							>Request Type:
						</span>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="transaction.requestType"
						/>
					</li>

					<!-- DEV NOTE: "API" data should be shown on all transaction types cards. Please add v-if and the Ellipsis :data -->
					<li
						class="transaction-details__details-item"
						v-if="transaction.apiType"
					>
						<span class="transaction-details__details-label"
							>API:
						</span>
						<Ellipsis
							class="transaction-details__details-txt"
							:data="getApiTypeAndVersion"
						/>
					</li>
				</ul>

				<div class="transaction-details__copy-details">
					<Button
						className="btn--has-icon btn--small"
						:iconClassName="'icon icon-copy'"
						:btnClick="copyDetails"
						labelText="Copy Details"
						:iconBeforeLabel="true"
						v-log="{
							event: 'click',
							target: 'TransactionAudit.DetailsCopy',
							eventData: () => this.logData('Details', 'Copied')
						}"
					/>
				</div>

				<hr class="transaction-details__horizontal-line" />

				<div class="transaction-details__payloads">
					<div class="transaction-details__payloads-header">
						<span class="transaction-details__payloads-title">
							Transaction Payloads
						</span>

						<Button
							className="btn btn--has-icon btn--small"
							labelText="Download All"
							:btnClick="downloadAll"
							:iconClassName="'icon icon-download'"
							:iconBeforeLabel="true"
							:disabled="showDownloadAll"
							v-log="{
								event: 'click',
								target: 'TransactionAudit.AllDownload',
								eventData: () =>
									this.logData('All', 'Downloaded')
							}"
						/>
					</div>

					<div class="transaction-details__raw-req-res">
						<RawTransaction
							class="transaction-details__payloads-raw"
							:log="detailLog"
							:auditInfo="logData(null, null)"
						/>
						<RequestTansaction
							class="transaction-details__payloads-request"
							:requestBlob="sendRequestBlob"
							:auditInfo="logData(null, null)"
						/>
						<ResponseTansaction
							class="transaction-details__payloads-response"
							:responseBlob="sendResponseBlob"
							:auditInfo="logData(null, null)"
						/>
					</div>
					<RelatedTansactions
						class="transaction-details__payloads-related"
						:correlationId="transactionCorrelationId"
						:excludeTransactionId="transactionId"
					/>
				</div>

				<NoResults v-if="!hasResultDetail && !loadingDetails" />
			</div>
		</ScrollContainer>
	</div>
</template>

<script>
import RelatedTansactions from './global-transactions-details-relatedtran';
import RequestTansaction from './global-transactions-details-request';
import ResponseTansaction from './global-transactions-details-response';
import RawTransaction from './global-transactions-details-rawtran';
import Button from '@/components/forms/button/button';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import Ellipsis from '@/components/visual/ellipsis';
import NotificationIcon from '@/components/notifications/notification-icon/notification-icon';
import Tooltip from '@/components/shells/tooltip/tooltip';
import { mapActions, mapGetters } from 'vuex';
import DatetimeMixin from '@/mixins/datetime-mixin';
import NoResults from '@/site-components/no-results/no-results';
import Spinner from '@/components/visual/spinner/spinner';
import CustomerOptions from '@/mixins/customers/customer-options';
import {
	logEntrytransactionTypes,
	Healths
} from '@/sections/transactions/shared/transactions-filters-params';
import { copyToClipboard } from '@/utils/function';
import BlobService from '@/services/blob-service';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import {
	MODULE_NAME as OPTIONS_MODULE_NAME,
	GET_CUSTOMER_OPTION_NAME,
	SET_COMPANIES
} from '@/sections/options/options-mutation-type';
import {
	MODULE_NAME as GLOBAL_MODULE_NAME,
	GLOBAL_TRANSACTIONS
} from '@/sections/transactions/transactions-mutation-types.js';
import { LoggingMixin } from 'vue-ui-layout';
import _ from 'lodash';
import store from '@/store';

export default {
	name: 'GlobalTransactionsDetails',
	mixins: [DatetimeMixin, CustomerOptions, LoggingMixin],
	components: {
		RawTransaction,
		RequestTansaction,
		ResponseTansaction,
		RelatedTansactions,
		Button,
		Breadcrumbs,
		Ellipsis,
		NotificationIcon,
		Tooltip,
		Spinner,
		NoResults,
		ScrollContainer
	},
	props: {
		transactionId: {
			type: String,
			required: true
		},
		health: {
			type: String,
			required: true // ask to Yoel. we don't use this prop
		}
	},
	data() {
		return {
			transaction: {},
			tooltipStatusVisible: false,
			loadingDetails: false,
			defaultUrlNames: [
				{
					url: `/transaction`,
					name: 'Global Transaction Viewer'
				},
				{
					name: 'Details'
				}
			]
		};
	},
	async created() {
		if (this.transactionId) {
			await this.fetchData();
		}
	},
	computed: {
		...mapGetters(GLOBAL_MODULE_NAME, [GLOBAL_TRANSACTIONS]),
		detailLog() {
			if (this.transaction.details) {
				return this.transaction.details;
			}
			return '';
		},
		sendRequestBlob() {
			if (this.transaction.details) {
				let details = JSON.parse(this.transaction.details);
				return JSON.stringify(details.RequestBlob);
			}
			return '';
		},
		sendResponseBlob() {
			if (this.transaction.details) {
				let details = JSON.parse(this.transaction.details);
				return JSON.stringify(details.ResponseBlob);
			}
			return '';
		},
		showDownloadAll() {
			return (
				_.isEmpty(this.sendRequestBlob) ||
				_.isEmpty(this.sendResponseBlob)
			);
		},
		transactionCorrelationId() {
			return this.transaction.correlationId || '';
		},
		isOEMTransactionType() {
			return (
				this.transaction.logTypeFormated == logEntrytransactionTypes.Oem
			);
		},
		isVueDmsInternalType() {
			return (
				this.transaction.logTypeFormated ==
				logEntrytransactionTypes.VueDmsInternal
			);
		},
		is3pVendorTransactionType() {
			return (
				this.transaction.logTypeFormated ==
				logEntrytransactionTypes.ThirdPartyVendor
			);
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlNames;
		},
		notificationVariantIcon() {
			const healthVariants = {
				Success: 'positive',
				Error: 'negative'
			};
			return healthVariants[this.transaction.health] || 'warning';
		},
		customerName() {
			return (
				this.transaction.dealershipGroupName ||
				this.transaction.customerName ||
				this[GET_CUSTOMER_OPTION_NAME](this.transaction.customerId) // TODO remove this logic after we wrap the data from this.transaction.DealershipGroupName
			);
		},
		hasResultDetail() {
			return !_.isEmpty(this.transaction);
		},
		displayMethod() {
			return !this.isOEMTransactionType
				? this.transaction.thirdPartyVendorMethod
				: this.transaction.oemMethod;
		},
		integratorName() {
			const integratorName =
				this.transaction.integratorName ||
				this.transaction.primaryIntegrationVendor;

			const integratorNameSplitted = integratorName.split('-');
			if (
				integratorNameSplitted.length > 1 &&
				integratorNameSplitted[0].toLowerCase().includes('motive')
			) {
				return integratorNameSplitted[1].trim();
			}
			return integratorName;
		},
		getApiTypeAndVersion(){
			return `${this.transaction.apiType}| ${this.transaction.apiVersion}`;
		}
	},
	methods: {
		...mapActions('globaltransactions', [
			'getGlobalTransactionById',
			'setGlobalTransactions'
		]),
		...mapActions(OPTIONS_MODULE_NAME, [SET_COMPANIES]),
		...mapActions('download', ['setDownloadAll']),

		async fetchData() {
			try {
				this.loadingDetails = true;

				const result = await this.getGlobalTransactionById({
					transactionId: this.transactionId,
					healths: this.health
				});

				if (!_.isEmpty(result.items)) {
					this.transaction = result.items[0];
				}
			} finally {
				this.loadingDetails = false;
			}
		},
		buildDetailDataLog() {
			let detailLog = {};
			detailLog.transactionID = this.transaction.transactionId;
			detailLog.correlationId = this.transaction.correlationId;
			detailLog.customer = this.customerName;
			detailLog.company = this.transaction.company;

			if (this.isOEMTransactionType) {
				//TODO check this
				detailLog.integration = this.transaction.logEntryTransactionType;
			}

			if (this.isVueDmsInternalType) {
				//TODO check this
				detailLog.userId = this.transaction.userId;
				detailLog.source = this.transaction.otherSource;
				detailLog.requestType = this.transaction.logEntryTransactionType;
			}

			if (!this.isVueDmsInternalType) {
				detailLog.method = this.transaction.thirdPartyVendorMethod;
				detailLog.direction = this.transaction.direction;
				detailLog.logType = this.transaction.logType;
			}

			if (
				!this.isVueDmsInternalType &&
				this.transaction.integrationName
			) {
				detailLog.integrationName = this.transaction.integrationName;
			}
			return detailLog;
		},
		copyDetails() {
			let detailLog = this.buildDetailDataLog();

			if (!_.isEmpty(detailLog)) {
				const formattedDetailLog = Object.entries(detailLog)
					.map(([key, value]) => `${key}: ${JSON.stringify(value)},`)
					.join('\n')
					.replace(/"/g, '');

				copyToClipboard(formattedDetailLog);

				store.dispatch('toastMessages/push', {
					statusCode: 200,
					message: 'Details copied to clipboard!!'
				});
			}
		},
		downloadAll() {
			return this.callApiDownloadAll();
		},
		async callApiDownloadAll() {
			const self = this;
			let query = {
				logId: self.transaction.transactionId,
				container: self.transaction.logType,
				level: Healths[self.transaction.health]
			};
			await this.setDownloadAll(query)
				.then(data => {
					BlobService.DownloadAll(
						data,
						'blob',
						self.transaction.transactionId
					);
				})
				.catch(error => {
					let val = {
						status: 400,
						message: error ? error : 'Failed to download all'
					};
					self.updateToast(val);
				});
		},
		logData(target, action) {
			const user = this.$store.getters['oidcStore/oidcUser'];
			// Important: LogDataObj object should be all key without null value.
			let logDataObj = {
				action: action,
				apiType:
					this.transaction.apiType ||
					this.transaction.apiTransactionType,
				companyId: this.transaction.companyId,
				companyName: this.transaction.companyName,
				companyNumber: this.transaction.company,
				correlationId: this.transaction.correlationId,
				dealershipGroupId: this.transaction.customerId,
				dealershipGroupName: this.transaction.dealershipGroupName,
				integratorName: this.transaction.integratorName,
				locationName: this.transaction.locationName,
				locationNumber: this.transaction.locationNumber,
				logEntryTransactionType: this.transaction
					.logEntryTransactionType,
				logEntryTransactionTypeName: this.transaction
					.logEntryTransactionTypeName,
				roleId: user.roleId,
				rolename: user.role,
				rolepermissions: user.SupportPermission,
				target: target,
				transactionId: this.transaction.transactionId,
				transactionType: this.transaction.transactionType,
				userId: user.sub,
				userfullname: user.GlobalFirstName + ' ' + user.GlobalLastName
			};
			return _.omitBy(logDataObj, _.isNil);
		}
	}
};
</script>

<style lang="scss" scoped>
/**
  * Transactions Details
  * Can use SCSS variables = TRUE
**/

.transaction-details {
	display: flex;
	flex-direction: column;
	background: $white;
	padding: $padding-lg $padding-lg $padding-lg $padding-md;
	border: 1px solid $neutral-3;
	border-radius: $form-border-radius;

	&__header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;

		&-primary {
			display: grid;
			grid-template-columns: auto 1fr;
			column-gap: $gutter-md;
		}

		&-title-group {
			display: flex;
			flex-direction: column;
			flex-wrap: wrap;
			gap: $gutter-sm;
		}

		&-type {
			@include heading('md');
			@include ellipsis;
			max-width: 260px;

			@include mq($from: md-l) {
				max-width: 360px;
			}
		}

		&-secondary {
			display: flex;
			flex-direction: column;
			@include body-text('regular');
			text-align: right;
		}
	}

	&__details {
		display: grid;
		grid-auto-flow: column;
		grid-template-columns: repeat(auto-fit, 1fr);
		grid-template-rows: repeat(4, 1fr);
		column-gap: $gutter-xl;
		row-gap: $gutter-md;
		padding-left: $padding-lg;
		margin-top: $gutter-xl;
	}

	&__details-item {
		@include body-text('regular');
		display: grid;
		grid-template-columns: auto 1fr;
		column-gap: $gutter-sm;
	}

	&__details-label {
		color: $neutral-2;
	}

	&__copy-details {
		display: flex;
		justify-content: flex-end;
		margin-top: $gutter-lg;
	}

	&__horizontal-line {
		@include create-horizontal-line(16px, 1px, $gutter-xl, $neutral-3);
	}

	&__payloads {
		&-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
		}

		&-title {
			@include heading('md');
		}

		&-raw {
			margin-top: $gutter-xl;
		}
	}

	//UI-UX NOTE: please update
	&__raw-req-res {
		display: flex;
		flex-direction: column;
		gap: $gutter-lg;
	}
}
</style>
